// FILE: src/components/app/bases/UploadDataComponent/UploadDataComponent.jsx

import React, { useState, useCallback, useEffect } from "react";
import UploadArea from "./UploadArea/UploadArea";
import DataTable from "./DataTable/DataTable";
import ProcessingDataComponent from "./ProcessingDataComponent/ProcessingDataComponent";
import { buildSegmentIndexFromRows } from "./utils/tagUtils";
import styles from "./UploadDataComponent.module.css";

// System formats via Firebase
import { SystemAPI } from "../../../../utils/firebase";

// Mapping modal + util
import UploadedDataSystemMappingModal from "./UploadedDataSystemMappingModal/UploadedDataSystemMappingModal";
import { applyMappingToRows } from "./utils/applyMapping";

export default function UploadDataComponent() {
  /* ======================= Local CSV state ======================= */
  const [rawHeaders, setRawHeaders] = useState([]);
  const [rawRows, setRawRows] = useState([]);

  // Pre-processing table (we keep RAW content here)
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);

  const [error, setError] = useState("");

  // Segment index (only meaningful with friendly headers that include ID/Tags)
  const [segmentIndex, setSegmentIndex] = useState(null);
  const [segmentVocab, setSegmentVocab] = useState([]);

  /* ======================= System formats state ======================= */
  const [fmtLoading, setFmtLoading] = useState(true);
  const [fmtError, setFmtError] = useState("");
  const [formats, setFormats] = useState({ transactions: null, accounts: null, tags: null });
  const [formatsFromCache, setFormatsFromCache] = useState({ transactions: null, accounts: null, tags: null });

  // Mapping state
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [mappingConfig, setMappingConfig] = useState(null); // { mappings, defaults, mappedTargets }

  /* ======================= Load system formats ======================= */
  const pickSystemFormat =
    SystemAPI?.pickSystemFormat ||
    ((docs, version = "v1") => {
      if (!Array.isArray(docs)) return null;
      return docs.find((d) => d?.id === version || d?.version === version) ?? docs[0] ?? null;
    });

  const loadSystemFormats = useCallback(async () => {
    setFmtError("");
    setFmtLoading(true);
    try {
      const [txRes, accRes, tagRes] = await Promise.all([
        SystemAPI.fetchSystemFormatsTransactionsWithMeta(),
        SystemAPI.fetchSystemFormatsAccountsWithMeta(),
        SystemAPI.fetchSystemFormatsTagsWithMeta(),
      ]);

      const txFmt = pickSystemFormat(txRes.value, "v1");
      const accFmt = pickSystemFormat(accRes.value, "v1");
      const tagFmt = pickSystemFormat(tagRes.value, "v1");

      setFormats({ transactions: txFmt, accounts: accFmt, tags: tagFmt });
      setFormatsFromCache({
        transactions: txRes.fromCache,
        accounts: accRes.fromCache,
        tags: tagRes.fromCache,
      });

      if (!txFmt?.fields || !accFmt?.fields || !tagFmt?.fields) {
        throw new Error("System formats are missing fields[]");
      }
    } catch (e) {
      console.error("[UploadData] Failed to load system formats", e);
      setFmtError("Failed to load system formats. Please refresh and try again.");
    } finally {
      setFmtLoading(false);
    }
  }, [pickSystemFormat]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try { await loadSystemFormats(); } finally { if (!alive) return; }
    })();
    return () => { alive = false; };
  }, [loadSystemFormats]);

  /* ======================= CSV parser ======================= */
  const parseCSV = useCallback((text, delimiter = ",") => {
    const out = [];
    let row = [];
    let field = "";
    let i = 0;
    let inQuotes = false;

    while (i < text.length) {
      const ch = text[i];

      if (inQuotes) {
        if (ch === '"') {
          if (text[i + 1] === '"') { field += '"'; i += 2; }
          else { inQuotes = false; i++; }
        } else { field += ch; i++; }
        continue;
      }

      if (ch === '"') { inQuotes = true; i++; continue; }
      if (ch === delimiter) { row.push(field); field = ""; i++; continue; }
      if (ch === "\n") { row.push(field); out.push(row); row = []; field = ""; i++; continue; }
      if (ch === "\r") { i++; continue; }
      field += ch; i++;
    }

    row.push(field);
    out.push(row);

    if (out.length && out[out.length - 1].every((c) => String(c ?? "").trim() === "")) out.pop();
    return out;
  }, []);

  const recomputeSegmentIndex = useCallback((hdrs, dataRows) => {
    const idColIndex = hdrs.indexOf("ID");
    const tagsColIndex = hdrs.indexOf("Tags");
    if (idColIndex >= 0 && tagsColIndex >= 0) {
      const { index, uniqueSegments } = buildSegmentIndexFromRows(dataRows, idColIndex, tagsColIndex);
      setSegmentIndex(index);
      setSegmentVocab(uniqueSegments);
    } else {
      setSegmentIndex(null);
      setSegmentVocab([]);
    }
  }, []);

  /* ======================= File select → parse → OPEN MODAL ======================= */
  const handleFileSelect = useCallback(
    async (file) => {
      setError("");
      setRawHeaders([]); setRawRows([]);
      setHeaders([]); setRows([]);
      setSegmentIndex(null); setSegmentVocab([]);
      setMappingConfig(null);

      if (!file) return;
      if (!file.type.includes("csv") && !/\.csv$/i.test(file.name)) {
        setError("Please choose a .csv file.");
        return;
      }

      try {
        const text = await file.text();
        const matrix = parseCSV(text, ",");
        if (!matrix.length) { setError("The file appears to be empty."); return; }

        const hdrs = matrix[0];
        const dataRows = matrix.slice(1).filter((r) => Array.isArray(r) && r.some((c) => String(c ?? "").trim() !== ""));

        setRawHeaders(hdrs);
        setRawRows(dataRows);

        // Immediately open mapping modal (table stays hidden until done)
        if (!fmtLoading && !fmtError && formats?.transactions) {
          setIsMapOpen(true);
        } else {
          const poll = setInterval(() => {
            if (!fmtLoading && !fmtError && formats?.transactions) {
              clearInterval(poll);
              setIsMapOpen(true);
            }
          }, 150);
          setTimeout(() => clearInterval(poll), 10000);
        }
      } catch (e) {
        console.error(e);
        setError("Failed to read file.");
      }
    },
    [parseCSV, fmtLoading, fmtError, formats]
  );

  /* ======================= Modal handlers ======================= */
  const handleMappingConfirm = useCallback(
    ({ mappings, defaults }) => {
      try {
        const { mappedTargets } = applyMappingToRows(rawHeaders, rawRows, mappings, defaults) || {};
        setMappingConfig({ mappings, defaults, mappedTargets: mappedTargets || [] });
      } catch {
        setMappingConfig({ mappings, defaults, mappedTargets: [] });
      }
      setIsMapOpen(false);

      // Now that mapping is done, show RAW preview table (unchanged) for review
      setHeaders(rawHeaders);
      setRows(rawRows);
      recomputeSegmentIndex(rawHeaders, rawRows);
    },
    [rawHeaders, rawRows, recomputeSegmentIndex]
  );

  // If user closes the modal without confirming, keep it open to enforce mapping first.
  const handleMappingClose = useCallback(() => {
    // Intentionally do nothing other than keep the modal open if we have a file.
    // If no raw data present (no file), we just hide.
    if (rawHeaders.length && rawRows.length) {
      setIsMapOpen(true);
    } else {
      setIsMapOpen(false);
    }
  }, [rawHeaders, rawRows]);

  /* ======================= Row actions (RAW preview) ======================= */
  const handleDeleteRow = useCallback((rowIndex) => {
    setRows((prev) => {
      const next = prev.filter((_, i) => i !== rowIndex);
      if (headers.length) recomputeSegmentIndex(headers, next);
      return next;
    });
  }, [headers, recomputeSegmentIndex]);

  const handleRemoveTagPath = useCallback(() => {}, []);
  const handleSaveRow = useCallback((rowIndex, updatedRowArray) => {
    setRows((prev) => {
      const next = [...prev];
      next[rowIndex] = updatedRowArray;
      if (headers.length) recomputeSegmentIndex(headers, next);
      return next;
    });
  }, [headers, recomputeSegmentIndex]);

  /* ======================= Render ======================= */
  const showTable = headers.length > 0 && rows.length > 0 && !isMapOpen;

  return (
    <div className="container-fluid">
      <div className="pp-page">
        {/* Header */}
        <div className="pp-page-header d-flex align-items-center justify-content-between">
          <h1 className="pp-page-title">Upload Data</h1>
          <div className="pp-page-actions">
            {fmtLoading && <span className="text-secondary small">Loading system formats…</span>}
            {!fmtLoading && fmtError && <span className="text-danger small">System formats failed to load</span>}
            {!fmtLoading && !fmtError && (
              <span className="text-secondary small">
                Formats v1 loaded{formatsFromCache.transactions ? " (cache)" : ""}
              </span>
            )}
          </div>
        </div>

        {/* Upload area */}
        <div className="pp-section">
          <p className="text-secondary mb-3">
            Upload a <code>.csv</code> export of your transactions. The first row should be headers.
          </p>
          <div className={styles.surfaceCard}>
            <UploadArea onFileSelect={handleFileSelect} />
          </div>
        </div>

        {error && <div className="alert alert-danger mt-3">{error}</div>}

        {/* RAW CSV preview appears only AFTER mapping modal is completed */}
        {showTable && (
          <>
            <div className="pp-section">
              <div className={`${styles.surfaceCard} ${styles.tableSurface} pp-table`}>
                <div className="pp-table-scroll">
                  <DataTable
                    headers={headers}
                    rows={rows}
                    onDeleteRow={handleDeleteRow}
                    onRemoveTagPath={handleRemoveTagPath}
                    onSaveRow={handleSaveRow}
                  />
                </div>
              </div>
            </div>

            <div className="pp-section">
              <div className={styles.surfaceCard}>
                <ProcessingDataComponent
                  headers={headers}                 // RAW headers
                  rows={rows}                       // RAW rows
                  systemFormats={formats}
                  mappingConfig={mappingConfig}     // used downstream
                  rawCsv={{ headers: rawHeaders, rows: rawRows }}
                />
              </div>
            </div>
          </>
        )}

        {/* Mapping Modal — blocks until confirmed */}
        <UploadedDataSystemMappingModal
          isOpen={isMapOpen}
          onClose={handleMappingClose}
          csvHeaders={rawHeaders}
          sampleRows={rawRows.slice(0, 12)}
          transactionFormat={formats.transactions}
          onConfirm={handleMappingConfirm}
        />
      </div>
    </div>
  );
}
